import { Claim } from '@wsphere/warranties/domain';

const State = Claim.Authorization.State;
const Readiness = Claim.PaymentReadiness;
const Reason = Claim.ContactReason;
const Approval = Claim.Override.State;

/** Ways to filter by an open hold's due date; days are counted from today. */
export type HoldDueOption = 'OVERDUE' | 'TODAY' | 'WEEK';

/** Copy for the claims queue filters and columns. */
export const CLAIM_QUEUE_CONFIG = {
  section: 'Needs attention',

  openHold: 'Has an open evidence hold',
  openEscalation: 'Has an open escalation',

  holdDue: {
    placeholder: 'Hold due',
    any: 'Any due date',
    options: [
      { value: 'OVERDUE', label: 'Overdue', days: -1 },
      { value: 'TODAY', label: 'Due by today', days: 0 },
      { value: 'WEEK', label: 'Due within 7 days', days: 7 },
    ] as { value: HoldDueOption; label: string; days: number }[],
  },

  repairDeadline: {
    placeholder: 'Repair deadline',
    any: 'Any repair deadline',
  },

  contactReason: {
    placeholder: 'Contact reason',
    labels: {
      [Reason.CLARIFYING_QUESTION]: 'Clarifying question',
      [Reason.MISSING_INFORMATION]: 'Missing information',
      [Reason.SHARE_DECISION]: 'Share a decision',
      [Reason.REPAIR_STATUS]: 'Repair status',
      [Reason.OTHER]: 'Other',
    } as Record<Claim.ContactReason, string>,
  },

  age: {
    placeholder: 'Age',
    any: 'Any age',
    options: [3, 7, 14, 30].map(days => ({ value: days, label: `Opened more than ${days} days ago` })),
  },

  authorization: {
    placeholder: 'Authorization',
    labels: {
      [State.EDITABLE]: 'Not authorized',
      [State.INACTIVE]: 'Closed, not authorized',
      [State.AUTHORIZED]: 'Authorized',
      [State.PAYMENT_READY]: 'Ready for payment',
      [State.NOT_REQUIRED]: 'No authorization needed',
    } as Record<Claim.Authorization.State, string>,
  },

  paymentReadiness: {
    placeholder: 'Payment readiness',
    labels: {
      [Readiness.NOT_READY]: 'Not ready',
      [Readiness.READY]: 'Ready to pay',
      [Readiness.HELD_BACK]: 'Held back for approval',
      [Readiness.IN_PROGRESS]: 'Payment in progress',
      [Readiness.PAID]: 'Paid',
    } as Record<Claim.PaymentReadiness, string>,
  },

  // Approvals on the current split or its authorization.
  approval: {
    placeholder: 'Approval',
    labels: {
      [Approval.PENDING]: 'Waiting for approval',
      [Approval.APPROVED]: 'Approved',
      [Approval.REJECTED]: 'Rejected',
      [Approval.REVERSED]: 'Approval reversed',
    } as Record<Claim.Override.State, string>,
  },

  columns: {
    attention: 'Attention',
    // Also the sort key the API understands.
    holdDue: Claim.QueueSort.HOLD_DUE,
    repairDeadline: Claim.QueueSort.REPAIR_DEADLINE,
    none: '-',
  },

  tags: {
    holds: (count: number) => `${count} open ${count === 1 ? 'hold' : 'holds'}`,
    overdue: 'Overdue hold',
    repairOverdue: 'Repair deadline passed',
    contact: (reason: string) => `Contact: ${reason}`,
    escalations: (count: number) => `${count} ${count === 1 ? 'escalation' : 'escalations'}`,
    approvals: (count: number) => (count === 1 ? 'Approval needed' : `${count} approvals needed`),
  },
};
